import { Plus, Pencil, Trash2, User } from 'lucide-react'
import type { MarketPersona } from '@ispot/shared'
import { ROLE_LABELS } from './PersonaDialog'

interface Props {
  personas: MarketPersona[]
  segmentCountByPersona: Map<string, number>
  onEdit: (p: MarketPersona) => void
  onDelete: (p: MarketPersona) => void
  onAdd: () => void
}

export function PersonaList({
  personas,
  segmentCountByPersona,
  onEdit,
  onDelete,
  onAdd,
}: Props) {
  return (
    <aside
      className="flex h-full flex-col border-r"
      style={{
        borderColor: 'var(--color-border-subtle)',
        backgroundColor: 'var(--color-warm-50)',
      }}
    >
      <div className="flex-1 overflow-y-auto px-3 py-3">
        {personas.length === 0 ? (
          <div
            className="rounded-lg px-3 py-6 text-center text-xs italic"
            style={{ color: 'var(--color-text-muted)' }}
          >
            No personas yet.
          </div>
        ) : (
          <ul className="space-y-1">
            {personas.map((p) => {
              const count = segmentCountByPersona.get(p.id) ?? 0
              return (
                <li key={p.id}>
                  <div
                    className="group flex items-center gap-2 rounded-md px-2 py-2"
                    style={{ backgroundColor: 'transparent' }}
                  >
                    <span
                      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                      style={{ backgroundColor: 'var(--color-business, #0984e3)' }}
                    >
                      <User className="h-3 w-3 text-white" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div
                        className="truncate text-xs font-semibold"
                        style={{ color: 'var(--color-text)' }}
                      >
                        {p.name}
                      </div>
                      <div
                        className="truncate text-[10px]"
                        style={{ color: 'var(--color-text-muted)' }}
                      >
                        {ROLE_LABELS[p.role]} · {count} segment{count === 1 ? '' : 's'}
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                      <button
                        type="button"
                        onClick={() => onEdit(p)}
                        className="rounded p-1 transition-colors hover:bg-[var(--color-warm-200)]"
                        aria-label={`Edit ${p.name}`}
                      >
                        <Pencil
                          className="h-3 w-3"
                          style={{ color: 'var(--color-text-muted)' }}
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(p)}
                        className="rounded p-1 transition-colors hover:bg-[var(--color-warm-200)]"
                        aria-label={`Delete ${p.name}`}
                      >
                        <Trash2
                          className="h-3 w-3"
                          style={{ color: 'var(--color-text-muted)' }}
                        />
                      </button>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
      <div
        className="border-t px-3 py-2"
        style={{ borderColor: 'var(--color-border-subtle)' }}
      >
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-md py-1.5 text-xs font-medium"
          style={{
            color: 'var(--color-text-secondary)',
            backgroundColor: 'transparent',
            border: '1px dashed var(--color-border)',
          }}
        >
          <Plus className="h-3 w-3" /> Add persona
        </button>
      </div>
    </aside>
  )
}
